import { useEffect, useRef, useState } from 'react';

import { useAIBudgetStatus } from '@/hooks/useAIBudget';
import { isBudgetExhausted } from '@/sections/insights/aiAssistant/utils/budgetGate';

interface UseHannibalSummonGateOptions {
  /**
   * The hunt wizard opener (from `useHuntMode`). The gate calls this only when
   * the pre-flight budget check passes, so a maxed-out budget can never open the
   * wizard.
   */
  setPendingMission: (mission: string) => void;
}

/**
 * Owns the Hannibal budget gate that sits in front of every wizard-open entry
 * point. Extracted out of the `AIAssistant.tsx` composition root so the page
 * stays a thin wiring layer (see this directory's CLAUDE.md "prefer extracting
 * into hooks/" guideline).
 *
 * Responsibilities:
 *   - Read the current user's AI budget (`useAIBudgetStatus`) — serves both the
 *     pre-flight threshold check and the modal figures.
 *   - Own the `budgetExhaustedOpen` modal state.
 *   - Expose `onBudgetExhausted` for the END guard (the backend's HTTP 402 on
 *     final launch, surfaced via `useHuntMode`).
 *   - Expose the single `handleSummonHannibal` gate that all four entry points
 *     route through (WelcomeView, Sidebar, HeaderBar, /hunt slash command).
 *
 * `budgetRefreshing` (Fix A): true ONLY while the 402-path background refetch is
 * in flight. The 402 means the backend's view is fresher than our 30s-cached
 * query, so we invalidate and skeleton the meter rather than flash stale figures
 * with a contradictory caption. The pre-flight 100% gate path never sets this —
 * its cached data already equals the at-rejection truth, so the meter renders
 * immediately with no skeleton flicker.
 */
export function useHannibalSummonGate({
  setPendingMission,
}: UseHannibalSummonGateOptions) {
  // Current authenticated user's AI budget (no email => non-impersonating).
  // Serves both the beginning gate (threshold) and the modal figures.
  const {
    data: aiBudget,
    invalidate: refetchAIBudget,
    isFetching,
  } = useAIBudgetStatus();
  const [budgetExhaustedOpen, setBudgetExhaustedOpen] = useState(false);

  // True from the moment the 402 path triggers a refetch until that refetch
  // resolves. Only the 402 path arms it; the pre-flight gate leaves it false so
  // its already-fresh cached figures render without a skeleton.
  const pendingRefreshRef = useRef(false);

  // The 402 means the backend's view is fresher than our 30s-cached query —
  // invalidate so the modal shows the at-rejection numbers. Don't await the
  // invalidate (that would delay the modal by a network round-trip); instead arm
  // `pendingRefreshRef` so the meter skeletons while the refresh is in flight and
  // never paints stale figures.
  const onBudgetExhausted = () => {
    pendingRefreshRef.current = true;
    refetchAIBudget();
    setBudgetExhaustedOpen(true);
  };

  // Self-disarm the 402 refresh flag once the refetch settles, so a later
  // background refetch (refetchOnWindowFocus after staleTime) can't re-skeleton
  // fresh figures. Effect runs post-commit, which is before any next refetch arms.
  useEffect(() => {
    if (!isFetching) pendingRefreshRef.current = false;
  }, [isFetching]);

  // Skeleton the meter only on the 402 path while its refetch is pending. Once
  // the refetch resolves, `isFetching` flips false and the real meter renders.
  // eslint-disable-next-line react-hooks/refs -- intentional render-phase READ to derive skeleton state; the WRITE is now in an effect
  const budgetRefreshing = pendingRefreshRef.current && isFetching;

  // BEGINNING gate (single source of truth): every path that opens the Launch
  // wizard routes through here so a maxed-out budget blocks ALL entry points,
  // not just one. Fails OPEN for loading/null/!set/degenerate states so the
  // normal summon flow never regresses. React Compiler is on (no useCallback).
  const handleSummonHannibal = (mission = '') => {
    if (isBudgetExhausted(aiBudget)) {
      // Pre-flight path: cached figures already equal the at-rejection truth, so
      // never skeleton (no refresh pending).
      pendingRefreshRef.current = false;
      setBudgetExhaustedOpen(true);
      return;
    }
    setPendingMission(mission);
  };

  return {
    handleSummonHannibal,
    onBudgetExhausted,
    budgetExhaustedOpen,
    setBudgetExhaustedOpen,
    aiBudget,
    budgetRefreshing,
  };
}
